<script lang="ts">
	import { Button, Container, IconButton, Select, Stack, Text } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import TimeInput from './TimeInput.svelte';
	import {
		RATE_OPTIONS,
		MAX_PERIODS_PER_DAY,
		rateSelectOptionForPeriod,
		handleSelectChange,
		type TariffTables,
		type DayPattern,
		type TimeRate,
	} from '../helpers/daisy_em_tariff_helpers';

	export let tariffTables: TariffTables;

	$: activeDayIndices = tariffTables.days.reduce<number[]>((indices, pattern, dayIdx) => {
		if (pattern.some(Boolean)) indices.push(dayIdx);
		return indices;
	}, []);

	$: rateSelectOptions = [1, 2, 3, 4].map((n) => ({
		value: `T${n}`,
		title: $format('daisy_em_tariff_tariff_model_option', {
			values: { n },
			default: `Tariff model ${n}`,
		}),
	}));

	const addDayPattern = () => {
		if (activeDayIndices.length >= 6) return;
		const pattern: DayPattern = [['01:00', 1], ...Array(MAX_PERIODS_PER_DAY - 1).fill(null)];
		const emptyIdx = tariffTables.days.findIndex((tablePattern) => !tablePattern.some(Boolean));
		if (emptyIdx >= 0) {
			tariffTables.days[emptyIdx] = pattern;
			tariffTables.days = [...tariffTables.days];
		} else {
			tariffTables.days = [...tariffTables.days, pattern];
		}
		tariffTables = { ...tariffTables };
	};

	/**
	 * Removes a day pattern by index and clamps all week/season references
	 * so no index points past the new array length.
	 * @param dayIdx - Index of the day pattern to remove.
	 */
	const removeDayPattern = (dayIdx: number) => {
		if (tariffTables.days.length <= 1) return;
		tariffTables.days = tariffTables.days.filter(
			(tablePattern, tablePatternIdx) => tablePatternIdx !== dayIdx,
		);
		tariffTables.weeks = tariffTables.weeks.map((week) =>
			week.map((d) => Math.min(d, tariffTables.days.length - 1)),
		);
		tariffTables.seasons = [
			Math.min(tariffTables.seasons[0], tariffTables.weeks.length - 1),
			Math.min(tariffTables.seasons[1], tariffTables.weeks.length - 1),
		];
		tariffTables = { ...tariffTables };
	};

	/**
	 * Updates the start time of a specific period slot, preserving its rate.
	 * @param dayIdx - Day pattern index.
	 * @param slotIdx - Period slot index within the day pattern.
	 * @param time - New time string in HH:MM format.
	 */
	const setPeriodTime = (dayIdx: number, slotIdx: number, time: string) => {
		const existing = tariffTables.days[dayIdx][slotIdx] ?? ['00:00', 1];
		tariffTables.days[dayIdx][slotIdx] = [time, existing[1]];
		tariffTables.days[dayIdx] = [...tariffTables.days[dayIdx]];
		tariffTables = { ...tariffTables };
	};

	/**
	 * Updates the tariff rate of a specific period slot, preserving its time.
	 * @param dayIdx - Day pattern index.
	 * @param slotIdx - Period slot index within the day pattern.
	 * @param rateLabel - Rate label (e.g. "T1"–"T4").
	 */
	const setPeriodRate = (dayIdx: number, slotIdx: number, rateLabel: string) => {
		const rate = RATE_OPTIONS.indexOf(rateLabel) + 1;
		const existing = tariffTables.days[dayIdx][slotIdx] ?? ['00:00', 1];
		tariffTables.days[dayIdx][slotIdx] = [existing[0], rate];
		tariffTables.days[dayIdx] = [...tariffTables.days[dayIdx]];
		tariffTables = { ...tariffTables };
	};

	/**
	 * Removes a period slot from a day pattern, compacting remaining active slots
	 * to the front and back-filling with nulls.
	 * @param dayIdx - Day pattern index.
	 * @param slotIdx - Period slot index to remove.
	 */
	const clearPeriod = (dayIdx: number, slotIdx: number) => {
		const active = tariffTables.days[dayIdx].filter(
			(p, i): p is TimeRate => p !== null && i !== slotIdx,
		);
		tariffTables.days[dayIdx] = [
			...active,
			...Array(MAX_PERIODS_PER_DAY - active.length).fill(null),
		] as DayPattern;
		tariffTables = { ...tariffTables };
	};

	/**
	 * Adds a default period (01:00, T1) to the first empty slot of a day pattern.
	 * @param dayIdx - Day pattern index.
	 */
	const addPeriod = (dayIdx: number) => {
		const pattern = tariffTables.days[dayIdx];
		const firstNull = pattern.findIndex((p) => p === null);
		if (firstNull === -1) return;
		pattern[firstNull] = ['01:00', 1];
		tariffTables.days[dayIdx] = [...pattern];
		tariffTables = { ...tariffTables };
	};

	const handlePeriodTimeChange = (dayIdx: number, slotIdx: number, e: CustomEvent<string>) =>
		setPeriodTime(dayIdx, slotIdx, e.detail);

	const handlePeriodRateChange = (dayIdx: number, slotIdx: number, e: CustomEvent) => {
		setPeriodRate(dayIdx, slotIdx, handleSelectChange(e));
	};
</script>

<Text styleType="h2" class="mb-s">
	{$format('daisy_em_tariff_day_patterns', { default: 'Day Patterns' })}
</Text>

{#each tariffTables.days as pattern, dayIdx (dayIdx)}
	{#if pattern.some(Boolean)}
		<Container type="borderSolid" backgroundColor="color-base" class="mb-s">
			<Text styleType="body-small" class="text-bold mb-xs">
				{$format('daisy_em_tariff_day_pattern_label', {
					values: { n: dayIdx + 1 },
					default: `Day Pattern ${dayIdx + 1}`,
				})}
			</Text>
			<Stack gap="xs" class="mb-s">
				{#each pattern as period, slotIdx}
					{#if period !== null}
						<div class="d-flex align-items-center gap-xs">
							<TimeInput
								id={`time-${dayIdx}-${slotIdx}`}
								value={period[0]}
								on:change={(e) => handlePeriodTimeChange(dayIdx, slotIdx, e)}
							/>
							<Select
								id={`rate-${dayIdx}-${slotIdx}`}
								aria-label={$format('daisy_em_tariff_period_tariff_model', {
									values: { day: dayIdx + 1, slot: slotIdx + 1 },
									default: 'Tariff model for this period',
								})}
								size="m"
								icon="fas fa-tags"
								value={rateSelectOptionForPeriod(rateSelectOptions, period)}
								options={rateSelectOptions}
								on:change={(e) => handlePeriodRateChange(dayIdx, slotIdx, e)}
							/>
							<IconButton
								variant="secondary"
								aria-label={$format('daisy_em_tariff_remove_period', {
									default: 'Remove period',
								})}
								icon="fas fa-trash"
								on:click={() => clearPeriod(dayIdx, slotIdx)}
							/>
						</div>
					{/if}
				{/each}
			</Stack>
			<Stack direction="column" gap="xs">
				{#if pattern.some((p) => p === null)}
					<Button
						size="s"
						variant="primary"
						styleType="ghost"
						icon="fas fa-plus"
						width="full"
						on:click={() => addPeriod(dayIdx)}
					>
						{$format('daisy_em_tariff_add_period', {
							default: 'Add day pattern',
						})}
					</Button>
				{/if}
				{#if tariffTables.days.length > 1}
					<Button
						size="s"
						variant="danger"
						styleType="ghost"
						width="full"
						on:click={() => removeDayPattern(dayIdx)}
					>
						{$format('daisy_em_tariff_remove_day_pattern', {
							default: 'Remove day pattern',
						})}
					</Button>
				{/if}
			</Stack>
		</Container>
	{/if}
{/each}

{#if activeDayIndices.length < 6}
	<Button
		size="m"
		styleType="secondary"
		width="full"
		class="mb-s"
		on:click={addDayPattern}
		icon="fas fa-plus"
	>
		{$format('daisy_em_tariff_add_day_pattern', { default: 'Add day pattern' })}
	</Button>
{/if}
